import type { TableConstructionProgress } from './constructionProgress';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryFunctionContext, QueryObserverOptions } from '@tanstack/query-core';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Bounds, FieldDescriptor, TableClient } from '@thinkalpha/table-client';
import type { Observable, ObservedValueOf } from 'rxjs';
import type { Snapshot } from 'src/api/getTableSnapshot';
import type { TableStatus } from 'src/components/table-view/model';
import type { ReactiveInjectable } from 'src/features/ioc';
import type { FullKeyType } from 'src/types';

export * from './constructionProgress';

/**
 * How to request a subscription (and possibly creation) of a table.
 */
export type TableCreationQuery = QueryObserverOptions<any, any, FullKeyType | undefined>;

/**
 * A table creation query can optionally receive the TableModel's debugging identifier.
 * The identifier is deliberately separate from the query key so it does not affect caching.
 */
export type TableQueryFunction<T = unknown> = (
    context: QueryFunctionContext,
    debugIdentifier?: string,
) => T | Promise<T>;

/**
 * Payload which is able to initialize a TableModel. Prefer the `Signal` form of `queryOptions`.
 */
export interface TableCreationOptions {
    initBounds?: Bounds;
    initThrottle?: number;
    /**
     * Descriptive name shown in Redux DevTools (e.g. `"Screener/results"`).
     * The final instance name will be `"TableModel: {devtoolsName} [{shortId}]"`.
     */
    devtoolsName?: string;
    /**
     * Prefer the `Signal` form of `queryOptions` for reactivity. The `setQueryOptions` imperative form is deprecated.
     */
    queryOptions: TableCreationQuery | Signal<TableCreationQuery>;
}

export interface SignalUpdate {
    update: ObservedValueOf<TableClient['update$']> | undefined;
}

export const NEW_TABLE_RESET = Symbol('NEW_TABLE_RESET');
export type NEW_TABLE_RESET = typeof NEW_TABLE_RESET;

export interface TableModel extends ReactiveInjectable {
    /**
     * The current bounds configuration of the TableModel
     */
    readonly bounds: Bounds;

    columns: OutputColumnWithMetadata[];
    columnsSignal: ReadonlySignal<OutputColumnWithMetadata[]>;
    /**
     * A signal that represents the current connection status of the table
     */
    connectionStatus: Signal<TableStatus>;

    count$: Observable<number>;
    /**
     * Field descriptors of the current table. Follows the TableClient across replacements,
     * including one rebuilt for the same key, and replays the latest value to late subscribers.
     */
    readonly descriptor$: Observable<FieldDescriptor[]>;
    /** The current table's JSON definition as the proxy reports it; follows and replays like `descriptor$`. */
    readonly definition$: Observable<unknown>;
    /**
     * A signal that represents the current row count of the table
     */
    countSignal: ReadonlySignal<number>;
    /**
     * A signal that tracks the number of table creation failures
     */
    failureCount: ReadonlySignal<number>;

    /**
     * Removes the existing table subscription and ignores reactive query
     * results until `refetch()` explicitly reactivates the model.
     */
    destroy(): void;

    error: Error | undefined;
    /**
     * Reactive form of `error` for model composition. Prefer this when a
     * consumer's lifecycle state depends on table-creation failures.
     */
    readonly errorSignal: ReadonlySignal<Error | undefined>;

    getColumnById(columnId: string): OutputColumnWithMetadata | null;

    getColumnUpdate$<T = unknown>(columnId: string): Observable<T>;

    init(tableCreationOptions: TableCreationOptions): void;

    /**
     * Manually triggers a refetch of the table creation query.
     * Useful for recovering from errors, particularly 400-series client errors
     * that do not auto-retry, and for reactivating a model after `destroy()`.
     */
    refetch(): void;

    /**
     * Clears the current table subscription while preserving reactive query
     * handling. A subsequent query-options change can attach a new table
     * without an explicit refetch.
     */
    reset(): void;

    key: string | null;
    /**
     * The current table key as a signal. Empty until a table client is attached.
     */
    readonly keySignal: ReadonlySignal<string>;
    /**
     * The current table construction state. `pending` means the initial key set
     * has not been processed; numbers are table-client's rounded percentage.
     */
    progress: Signal<TableConstructionProgress>;
    /**
     * Emits the current table construction state, or undefined when progress is
     * unavailable.
     */
    progress$: Observable<TableConstructionProgress>;
    /**
     * Whether the current table has finished construction by the status icon's rule
     * (`isTableConstructionComplete`): its initial key set is processed and table-client's rounded
     * percentage reached 100, whatever `enable-enhanced-table-progress` says. The rounding reads
     * 99.5% of cells bound as complete; PLAT-13582 asks table-client for exact completion. False
     * until the current table reports progress, so a table that never reports it, such as a
     * next-generation feed session, never completes.
     */
    readonly constructionComplete: ReadonlySignal<boolean>;
    /**
     * The server UUID (tsId) for this table connection
     */
    serverUUID: string | null;

    setBounds(bounds: Bounds): void;

    /**
     * Set a descriptive name for this TableModel in Redux DevTools.
     * The instance name becomes `"TableModel: {name} [{shortId}]"`.
     */
    setDevtoolsName(name: string): void;

    setThrottle(throttle: number | undefined, getMoreData: boolean): void;

    snapshot(opts: { firstRow?: number; windowSize?: number }): Promise<Snapshot | null>;

    waitForTableConnection(): Promise<void>;

    /** Whether the current table is a scrolling (infrastructure log) table. */
    readonly isScrolling: ReadonlySignal<boolean>;

    sortable: boolean;
    /**
     * @deprecated - TODO: Remove before PR merge if possible
     */
    tableClient: TableClient | null;

    /**
     * The current throttle configuration of the TableModel
     */
    readonly throttle: number | undefined;
    update$: TableClient['update$'];
    updates$: TableClient['updates$'];
    updateSignal: ReadonlySignal<SignalUpdate>;
    updateWithReset$: Observable<ObservedValueOf<TableClient['update$']> | NEW_TABLE_RESET>;
    updatesWithReset$: Observable<ObservedValueOf<TableClient['updates$']> | NEW_TABLE_RESET>;

    /**
     * @deprecated Prefer the Signal form of `queryOptions` in `init()` for reactivity.
     * This imperative API exists for legacy code that cannot easily adopt signals.
     */
    updateQueryOptions(queryOptions: QueryObserverOptions<any, any, FullKeyType>): void;
}

export * from './optimization';
